import { useState, useMemo } from 'react';
import { X, CreditCard, User, Phone, Mail, FileText, Save, AlertCircle, Lock, CheckCircle, XCircle, Search } from 'lucide-react';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { useData } from '../../../contexts/DataContext';

const STATUSES = [
  { value: 'active', label: 'Active', color: colors.green, icon: CheckCircle, description: 'Card is active and can be used' },
  { value: 'locked', label: 'Locked', color: colors.orange, icon: Lock, description: 'Card is temporarily locked' },
  { value: 'closed', label: 'Closed', color: colors.red, icon: XCircle, description: 'Card is permanently deactivated' },
];

export default function CardEditor({ card, isNew, onClose }) {
  const { createCard, updateCard, getAllUsers, assignCardsToUser, users } = useData();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const [formData, setFormData] = useState({
    cardLast4: card?.cardLast4 || '',
    holderName: card?.holderName || '',
    cardName: card?.cardName || '',
    hasAgreement: card?.hasAgreement || false,
    phone: card?.phone || '',
    email: card?.email || '',
    status: card?.status || 'active',
    notes: card?.notes || '',
  });

  // Find currently assigned user
  const assignedUser = useMemo(() => {
    if (!card) return null;
    return users.find(u =>
      (u.assignedCards || []).includes(card.cardLast4)
    );
  }, [card, users]);

  const [selectedUserId, setSelectedUserId] = useState(assignedUser?.uid || null);

  // Get available users for assignment
  const availableUsers = useMemo(() => {
    let result = getAllUsers().filter(u => u.status === 'active');

    if (userSearch) {
      const term = userSearch.toLowerCase();
      result = result.filter(u =>
        (u.displayName || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term)
      );
    }

    return result;
  }, [getAllUsers, userSearch]);

  const handleSave = async () => {
    setError(null);

    // Validation
    if (isNew && !formData.cardLast4) {
      setError('Card last 4 digits are required');
      return;
    }

    if (isNew && !/^\d{4}$/.test(formData.cardLast4)) {
      setError('Card must be exactly 4 digits');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await createCard({
          cardLast4: formData.cardLast4,
          holderName: formData.holderName || null,
          cardName: formData.cardName || null,
          hasAgreement: formData.hasAgreement,
          phone: formData.phone || null,
          email: formData.email || null,
          status: formData.status,
          notes: formData.notes || null,
        });

        // Assign to user if selected
        if (selectedUserId) {
          const targetUser = users.find(u => u.uid === selectedUserId);
          if (targetUser) {
            const newCards = [...(targetUser.assignedCards || []), formData.cardLast4];
            await assignCardsToUser(selectedUserId, newCards);
          }
        }
      } else {
        await updateCard(card.cardLast4, {
          holderName: formData.holderName || null,
          cardName: formData.cardName || null,
          hasAgreement: formData.hasAgreement,
          phone: formData.phone || null,
          email: formData.email || null,
          status: formData.status,
          notes: formData.notes || null,
        });

        // Handle user assignment changes
        const previousUserId = assignedUser?.uid;
        if (previousUserId !== selectedUserId) {
          // Remove from previous user
          if (previousUserId) {
            const prevUser = users.find(u => u.uid === previousUserId);
            if (prevUser) {
              const newCards = (prevUser.assignedCards || []).filter(c => c !== card.cardLast4);
              await assignCardsToUser(previousUserId, newCards);
            }
          }

          // Add to new user
          if (selectedUserId) {
            const newUser = users.find(u => u.uid === selectedUserId);
            if (newUser) {
              const newCards = [...(newUser.assignedCards || []), card.cardLast4];
              await assignCardsToUser(selectedUserId, newCards);
            }
          }
        }
      }

      onClose();
    } catch (err) {
      console.error('Error saving card:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedUserData = selectedUserId
    ? users.find(u => u.uid === selectedUserId)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-2xl"
        style={{ background: colors.cardBg }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: colors.border }}>
          <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
            {isNew ? 'Add New Card' : `Edit Card ****${card.cardLast4}`}
          </h2>
          <button onClick={onClose} style={{ color: colors.textMuted }}>
            <X size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 rounded-lg flex items-center gap-2" style={{ background: colors.redDim }}>
            <AlertCircle size={18} style={{ color: colors.red }} />
            <span style={{ color: colors.red }}>{error}</span>
          </div>
        )}

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Card Last 4 */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Card Last 4 Digits
            </label>
            <div className="relative">
              <CreditCard
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: colors.textMuted }}
              />
              <input
                type="text"
                value={formData.cardLast4}
                onChange={(e) => setFormData(prev => ({ ...prev, cardLast4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                disabled={!isNew}
                maxLength={4}
                className="w-full pl-10 pr-4 py-2 rounded-lg outline-none font-mono"
                style={{
                  background: isNew ? colors.inputBg : colors.black,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                  opacity: isNew ? 1 : 0.6,
                }}
                placeholder="1234"
              />
            </div>
            {!isNew && (
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                Card number cannot be changed
              </p>
            )}
          </div>

          {/* Holder Name */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Who Physically Has the Card
            </label>
            <div className="relative">
              <User
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: colors.textMuted }}
              />
              <input
                type="text"
                value={formData.holderName}
                onChange={(e) => setFormData(prev => ({ ...prev, holderName: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
                style={{
                  background: colors.inputBg,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                }}
                placeholder="John Smith"
              />
            </div>
          </div>

          {/* Card Name */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Name on Card
            </label>
            <input
              type="text"
              value={formData.cardName}
              onChange={(e) => setFormData(prev => ({ ...prev, cardName: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg outline-none"
              style={{
                background: colors.inputBg,
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
              }}
              placeholder="JOHN SMITH"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Status
            </label>
            <div className="space-y-2">
              {STATUSES.map(status => {
                const StatusIcon = status.icon;
                return (
                  <label
                    key={status.value}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                    style={{
                      background: formData.status === status.value ? status.color + '20' : colors.inputBg,
                      border: `1px solid ${formData.status === status.value ? status.color : colors.border}`,
                    }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={status.value}
                      checked={formData.status === status.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    />
                    <StatusIcon size={18} style={{ color: status.color }} />
                    <div>
                      <p className="font-medium" style={{ color: colors.textPrimary }}>{status.label}</p>
                      <p className="text-sm" style={{ color: colors.textMuted }}>{status.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Assigned User */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              <User size={14} className="inline mr-1" />
              Assigned To
            </label>

            {selectedUserData ? (
              <div
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: colors.inputBg }}
              >
                <div className="flex items-center gap-2">
                  {selectedUserData.photoUrl ? (
                    <img
                      src={selectedUserData.photoUrl}
                      alt={selectedUserData.displayName}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: colors.cardBg }}
                    >
                      <User size={16} style={{ color: colors.textMuted }} />
                    </div>
                  )}
                  <div>
                    <p style={{ color: colors.textPrimary }}>{selectedUserData.displayName || 'No name'}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{selectedUserData.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUserId(null)}
                  className="p-1"
                  style={{ color: colors.red }}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowUserPicker(!showUserPicker)}
                className="w-full p-3 rounded-lg text-left flex items-center justify-between"
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <span style={{ color: colors.textMuted }}>
                  No user assigned
                </span>
                <span style={{ color: colors.yellow }}>Assign</span>
              </button>
            )}

            {showUserPicker && (
              <div className="mt-2 rounded-lg p-3" style={{ background: colors.inputBg }}>
                <div className="relative mb-2">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: colors.textMuted }}
                  />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg text-sm outline-none"
                    style={{
                      background: colors.cardBg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                    }}
                  />
                </div>
                <div className="max-h-40 overflow-auto space-y-1">
                  {availableUsers.map(user => (
                    <div
                      key={user.uid}
                      className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:opacity-80"
                      style={{ background: colors.cardBg }}
                      onClick={() => {
                        setSelectedUserId(user.uid);
                        setShowUserPicker(false);
                        setUserSearch('');
                      }}
                    >
                      <span style={{ color: colors.textPrimary }}>
                        {user.displayName || user.email}
                      </span>
                      <span className="text-xs" style={{ color: colors.textMuted }}>
                        {user.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                Phone
              </label>
              <div className="relative">
                <Phone
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: colors.textMuted }}
                />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full pl-9 pr-3 py-2 rounded-lg outline-none text-sm"
                  style={{
                    background: colors.inputBg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`,
                  }}
                  placeholder="(414) 555-1234"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                Email
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: colors.textMuted }}
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-9 pr-3 py-2 rounded-lg outline-none text-sm"
                  style={{
                    background: colors.inputBg,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.border}`,
                  }}
                  placeholder="user@example.com"
                />
              </div>
            </div>
          </div>

          {/* Agreement */}
          <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer" style={{ background: colors.inputBg }}>
            <input
              type="checkbox"
              checked={formData.hasAgreement}
              onChange={(e) => setFormData(prev => ({ ...prev, hasAgreement: e.target.checked }))}
            />
            <div>
              <p className="font-medium" style={{ color: colors.textPrimary }}>CC Agreement on File</p>
              <p className="text-sm" style={{ color: colors.textMuted }}>Cardholder has signed agreement</p>
            </div>
          </label>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 rounded-lg outline-none resize-none"
              style={{
                background: colors.inputBg,
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
              }}
              placeholder="Any additional notes..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t" style={{ borderColor: colors.border }}>
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1">
            {saving ? <LoadingSpinner size="sm" color={colors.black} /> : <Save size={18} />}
            {isNew ? 'Create Card' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
